import { SecondaryButton } from '@/app/components/SecondaryButton'
import type { IHomePageSubCategoryWithCoursesHit } from '@/types/search'
import { useTranslations } from 'next-intl'
import React, { ComponentProps, Fragment, useLayoutEffect } from 'react'
import { useHits, useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { useIntersectionObserver } from 'usehooks-ts'
import { GroupItemsSection } from '../../search/components/GroupItemsSection'
import { NoResultsState } from '../../search/components/NoResultsState'
import { SearchErrorState } from '../../search/components/SearchErrorState'
import { SearchLoadingState } from '../../search/components/SearchLoadingState'
import { adaptCourseCollectionToCourseCard } from '../../search/lib/course-adapter'
import { groupHitsBySubCategory } from '../../search/lib/group-hits'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { NewCourseCard } from './NewCourseCard'

export type SubCategoryCoursesHitsProps = ComponentProps<'div'> & {
  splitGroupsSize?: number
  infiniteScroll?: boolean
}

export function SubCategoryCoursesHits({
  children,
  splitGroupsSize,
  infiniteScroll = false,
  ...props
}: SubCategoryCoursesHitsProps) {
  const tCommon = useTranslations('common')
  const { currentLocale } = useLanguageSelection()

  const regularHits =
    useHits<IHomePageSubCategoryWithCoursesHit<typeof currentLocale>>()
  const infiniteHits =
    useInfiniteHits<IHomePageSubCategoryWithCoursesHit<typeof currentLocale>>()

  const items = infiniteScroll ? infiniteHits.items : regularHits.items
  const hits = items || []

  const groupedItems = groupHitsBySubCategory(hits)

  const { status, refresh, setUiState } = useInstantSearch()

  const isStalled = status === 'stalled'
  const isLoading = status === 'loading'
  const isError = status === 'error'

  // Infinite scroll logic
  const { isIntersecting: isVisible, ref } = useIntersectionObserver({
    initialIsIntersecting: false,
  })

  const { isLastPage, showMore } = infiniteHits

  useLayoutEffect(() => {
    if (!infiniteScroll) return
    if (status !== 'idle' || isLastPage || !isVisible) {
      return
    }

    showMore()
  }, [infiniteScroll, isVisible, isLastPage, status, showMore])

  const handleErrorRetry = () => {
    refresh()
  }

  const handleNoResultsRetry = () => {
    setUiState({})
  }

  const childrenItems = React.Children.toArray(children)
  const subCategorySlug = items?.at(0)?.subcategory?.slug ?? ''

  return (
    <>
      {isError ? (
        <SearchErrorState
          onRetry={handleErrorRetry}
          className="md:min-h-[65vh]"
        />
      ) : (isLoading || isStalled) && !hits.length ? (
        <SearchLoadingState
          {...props}
          pageSize={splitGroupsSize || 10}
          className={infiniteScroll ? 'flex flex-col gap-12' : undefined}
        />
      ) : !isLoading && !hits.length ? (
        <NoResultsState
          onRetry={handleNoResultsRetry}
          className="md:min-h-[65vh]"
        />
      ) : (
        <>
          <div {...props}>
            {Object.entries(groupedItems).map(
              ([subCategoryId, { courses, name }], index) => (
                <Fragment key={subCategoryId}>
                  {splitGroupsSize &&
                    (index + 1) % splitGroupsSize === 0 &&
                    childrenItems[Math.floor(index / splitGroupsSize)]}
                  <GroupItemsSection
                    title={name}
                    header={
                      <SecondaryButton
                        href={`/subcategory/${subCategorySlug}`}
                        className="self-start"
                      >
                        {tCommon('view_more')}
                      </SecondaryButton>
                    }
                  >
                    {courses.map((course) => (
                      <NewCourseCard
                        key={course.id}
                        course={adaptCourseCollectionToCourseCard<
                          typeof currentLocale
                        >(course, currentLocale)}
                        className="h-full"
                      />
                    ))}
                  </GroupItemsSection>
                </Fragment>
              )
            )}
          </div>
          {infiniteScroll && (isLoading || isStalled) && (
            <SearchLoadingState className="flex flex-col gap-12" bare />
          )}
          {infiniteScroll && hits.length > 0 && !infiniteHits.isLastPage && (
            <div ref={ref} aria-hidden="true" />
          )}
        </>
      )}
    </>
  )
}
